import React, { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { i18next } from '@/config/i18n';
import { isEqual, sortBy } from 'underscore';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import ColumnHeaderCell from '../../Plannings/ColumnHeaderCell';
import { AppDispatch, RootState } from '../../../index';
import { Operator } from '../../Plannings/TableFilter';
import { localDateTime } from '../../Common/timeUtils';
import { setSelectedProcess, SetRecentPlanningDate, setSelectedVersion } from '@/actions/common/commonPure';
import { setPagination } from '@/actions/routes/routesPure';
import { Planning } from '@/config/types/Common';
import { processPagePath } from '@/config/paths';
import { ColumnKey, PlanningColumn, columns } from './columns';
import { DateOperations } from './utils';
import processApi from '@/apis/processApi';
import { setProcessHistory, setHistoryLoading, setProcessPageCache } from '@/actions/plannings/pure';
import { SET_CURRENT_PAGE, SET_TOTAL_PAGES } from '@/actions/types';

type Props = {
  minWidth?: number;
  closeModal?: () => void;
};

export type Filter = {
  id: ColumnKey;
  op: Operator;
  value: any;
};

export type RefsType = { [key: string]: RefObject<HTMLTableRowElement> };

type Sorting = {
  id: ColumnKey;
  direction: 'asc' | 'desc';
};

const ROWS_PER_PAGE = 20;

// Human labels for the backend SourceType enum ('initial' | 'modify' | 'replan').
const SOURCE_LABELS: Record<string, string> = {
  initial: 'Initial',
  modify: 'Modified',
  replan: 'Replan',
};

export const PlanningTable = (props: Props) => {
  const { closeModal } = props;

  const { selected_process, selected_version } = useSelector((state: RootState) => state.core);
  const {
    processHistory,
    loading,
    currentPage,
    totalPages,
    history,
    totalPlanningsCount,
    pageCache
  } = useSelector((state: RootState) => state.planning, isEqual);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [sorting, setSorting] = useState<Sorting>({ id: 'timestamp', direction: 'desc' });
  const [page, setPage] = useState<number>(currentPage - 1);
  const [plannings, setPlannings] = useState<Planning[]>([]);
  const columnsToDisplay = useMemo(() => columns(props.minWidth), [processHistory.length]);

  // Search state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searching, setSearching] = useState<boolean>(false);

  // Nested version rows: which optimization row is expanded, and its lazily
  // fetched versions (keyed by optimization_id).
  const [expandedOpt, setExpandedOpt] = useState<string | null>(null);
  const [versionsByOpt, setVersionsByOpt] = useState<Record<string, any[]>>({});
  const [versionsLoading, setVersionsLoading] = useState<Record<string, boolean>>({});
  const [versionsError, setVersionsError] = useState<Record<string, boolean>>({});

  const fetchVersions = async (optId: string) => {
    setVersionsLoading((s) => ({ ...s, [optId]: true }));
    setVersionsError((s) => ({ ...s, [optId]: false }));
    try {
      const res = await processApi.getOptimizationVersions(optId);
      setVersionsByOpt((s) => ({ ...s, [optId]: res?.data?.results ?? [] }));
    } catch {
      // Don't cache anything on failure — the next expand (or the retry row)
      // fetches again instead of permanently showing "No versions.".
      setVersionsError((s) => ({ ...s, [optId]: true }));
    } finally {
      setVersionsLoading((s) => ({ ...s, [optId]: false }));
    }
  };

  const toggleExpand = (optId: string) => {
    if (expandedOpt === optId) {
      setExpandedOpt(null);
      return;
    }
    setExpandedOpt(optId);
    if (!versionsByOpt[optId] && !versionsLoading[optId]) fetchVersions(optId);
  };

  // Expanded version sub-rows for one optimization. Newest first; the head is the
  // "latest". The initial run (null version_id) is only selectable when it IS the
  // latest, since the backend can't address it otherwise yet.
  const renderVersionRows = (
    optId: string,
    row: Planning,
    isSelectedOpt: boolean,
    colCount: number,
  ) => {
    if (versionsLoading[optId]) {
      return (
        <TableRow>
          <TableCell />
          <TableCell colSpan={colCount}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} />
              <span>{i18next.t('common:planning.table.loadingVersions')}</span>
            </Stack>
          </TableCell>
        </TableRow>
      );
    }
    if (versionsError[optId]) {
      return (
        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => fetchVersions(optId)}>
          <TableCell />
          <TableCell colSpan={colCount} sx={{ color: 'error.main', fontSize: 13 }}>
            {i18next.t('common:planning.table.versionsError')}
          </TableCell>
        </TableRow>
      );
    }
    const versions = versionsByOpt[optId] ?? [];
    const sorted = [...versions].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
    if (!sorted.length) {
      return (
        <TableRow>
          <TableCell />
          <TableCell colSpan={colCount} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            {i18next.t('common:planning.table.noVersions')}
          </TableCell>
        </TableRow>
      );
    }
    return sorted.map((v, i) => {
      const isLatest = i === 0;
      const addressable = v.version_id != null || isLatest;
      const isSelected =
        isSelectedOpt &&
        (selected_version === v.version_id || (selected_version == null && isLatest));
      return (
        <TableRow
          key={v.version_id ?? `initial-${i}`}
          hover={addressable}
          selected={isSelected}
          sx={{
            cursor: addressable ? 'pointer' : 'default',
            backgroundColor: 'action.hover',
            opacity: addressable ? 1 : 0.5,
          }}
          onClick={
            addressable ? () => onSelectDispatchProcess(row, v.version_id ?? null) : undefined
          }
        >
          <TableCell />
          <TableCell colSpan={colCount} sx={{ pl: 4, fontSize: 13 }}>
            {SOURCE_LABELS[v.source_type] ?? v.source_type} · {localDateTime(new Date(v.timestamp))}
            {isLatest ? ' (latest)' : ''}
            {!addressable ? ' — unavailable' : ''}
          </TableCell>
        </TableRow>
      );
    });
  };

  // Sync local page with Redux page
  useEffect(() => {
    setPage(currentPage - 1);
  }, [currentPage]);

  // ====== Data fetch (page-wise) ======
  const fetchPage = async (pageNumber: number) => {
    // First check the cache
    if (pageCache?.[pageNumber]) {
      dispatch(setProcessHistory(pageCache[pageNumber]));
      dispatch({ type: SET_CURRENT_PAGE, data: pageNumber });
      return;
    }
    dispatch(setHistoryLoading(true));
    try {
      const { data } = await processApi.getProcessIds(pageNumber);
      dispatch(setProcessHistory(data.results));
      dispatch(setProcessPageCache(pageNumber, data.results));
      dispatch({ type: SET_TOTAL_PAGES, data: data.total_pages });
      dispatch({ type: SET_CURRENT_PAGE, data: pageNumber });
    } catch (error) {
      console.error('Failed to fetch process page:', error);
    } finally {
      dispatch(setHistoryLoading(false));
    }
  };

  // Ensure a page is in Redux & cache
  const ensurePageLoaded = async (pageNumber: number) => {
    if (!pageCache?.[pageNumber]) {
      await fetchPage(pageNumber);
    } else {
      dispatch(setProcessHistory(pageCache[pageNumber]));
      dispatch({ type: SET_CURRENT_PAGE, data: pageNumber });
    }
  };

  // On mount, fetch if empty
  useEffect(() => {
    if (!processHistory.length) {
      fetchPage(currentPage);
    }
  }, []);

  // Handle page change via pagination component
  const onPageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
    fetchPage(newPage + 1); // backend is 1-indexed
  };

  const onSelectDispatchProcess = useCallback(
    // versionId undefined = a main-row click: leave the version to the reducer
    // (kept when re-selecting the same plan, reset to latest for a new plan).
    // versionId string|null = a version-row click: pin exactly that version
    // (null = the initial run, i.e. latest).
    (process: Planning, versionId?: string | null) => {
      dispatch(
        setPagination({
          objects: [],
          num_results: 0,
          page: 1,
          total_pages: 0,
          results_per_page: ROWS_PER_PAGE,
        })
      );
      dispatch(setSelectedProcess(process));
      dispatch(SetRecentPlanningDate(process.timestamp));
      if (versionId !== undefined) dispatch(setSelectedVersion(versionId));
      // The URL owns the selected plan on per-process pages; App syncs the store from it.
      const nextPath = processPagePath(pathname, process.optimization_id);
      if (nextPath) navigate(nextPath);
      closeModal?.();
    },
    [dispatch, closeModal, navigate, pathname]
  );

  // Apply filtering
  useEffect(() => {
    if (history.filters.length && processHistory.length) {
      let filteredPlannings = [...processHistory];
      history.filters.forEach((filter: Filter) => {
        filteredPlannings = onFilter(filter, filteredPlannings);
      });
      setPlannings(filteredPlannings);
    } else {
      setPlannings(processHistory);
    }
  }, [history, processHistory]);

  // Apply sorting
  useEffect(() => {
    let sorted = plannings;
    if (plannings.length) {
      sorted = sortBy(plannings, (val: Planning) => {
        return sorting.id === 'timestamp'
          ? val.timestamp?.valueOf()
          : (val as any)[sorting.id];
      });
      if (sorting.direction === 'desc') sorted.reverse();
      dispatch(SetRecentPlanningDate(sorted[0]?.timestamp));
      setPlannings(sorted);
    }
  }, [sorting, plannings.length]);

  const onSort = (id: ColumnKey) => {
    const direction = sorting.id === id && sorting.direction === 'asc' ? 'desc' : 'asc';
    setSorting({ id, direction });
  };

  const onFilter = (filter: Filter, list: Planning[]): Planning[] => {
    switch (filter.id) {
    case 'timestamp':
      return list.filter((planning) =>
        DateOperations[filter.op](
          new Date(planning.timestamp),
          new Date(filter.value),
          false
        )
      );
    case 'optimization_id':
      return list.filter((planning) =>
        planning.optimization_id.includes(filter.value)
      );
    default:
      return list;
    }
  };

  // ====== Global Search by optimization_id ======
  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) return;
    setSearching(true);

    try {
      // 1) Fast path: if backend has a direct finder
      try {
        // Expected response shape: { page: number, process: Planning }
        // If your API name/shape is different, adjust accordingly.
        const { data } = await (processApi as any).findProcessByOptimizationId(term);
        if (data?.page && data?.process) {
          await ensurePageLoaded(data.page);
          setPage(data.page - 1);
          onSelectDispatchProcess(data.process);
          setSearching(false);
          return;
        }
      } catch {
        // ignore and fallback to scanning
      }

      // 2) Check already cached pages
      if (pageCache) {
        for (const [pgStr, rows] of Object.entries(pageCache)) {
          const found = (rows as Planning[]).find(p => p.optimization_id === term);
          if (found) {
            const pg = Number(pgStr);
            await ensurePageLoaded(pg);
            setPage(pg - 1);
            onSelectDispatchProcess(found);
            setSearching(false);
            return;
          }
        }
      }

      // 3) Scan remaining pages
      for (let pg = 1; pg <= (totalPages || 1); pg++) {
        if (!pageCache?.[pg]) {
          const { data } = await processApi.getProcessIds(pg);
          dispatch(setProcessPageCache(pg, data.results));
          if (!totalPages) dispatch({ type: SET_TOTAL_PAGES, data: data.total_pages });
        }
        const rows = pageCache?.[pg] || [];
        const found = rows.find((p: Planning) => p.optimization_id === term);
        if (found) {
          await ensurePageLoaded(pg);
          setPage(pg - 1);
          onSelectDispatchProcess(found);
          setSearching(false);
          return;
        }
      }

      // Not found: optional UX — you can show a toast/snackbar here
      console.warn('No planning found for id:', term);
    } finally {
      setSearching(false);
    }
  };

  return (
    <Box height={1} width={1} style={{ position: 'relative' }}>
      {loading ? (
        <CircularProgress size={30} style={{ position: 'absolute', top: '220px', left: '395px' }} />
      ) : (
        <Paper sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
          {/* Pagination + Search bar in one row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ borderBottom: '1px solid lightgrey', px: 1 }}
          >
            <TablePagination
              data-cy={'table-pagination'}
              sx={{ height: 52 }}
              rowsPerPageOptions={[]}
              component="div"
              count={totalPlanningsCount}
              rowsPerPage={ROWS_PER_PAGE}
              page={page}
              onPageChange={onPageChange}
            />

            <TextField
              size="small"
              placeholder={i18next.t('common:planning.table.searchOptimizationId')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="search"
                      onClick={handleSearch}
                      disabled={!searchTerm || searching}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: 280, mr: 1 }}
            />
          </Stack>

          <TableContainer sx={{ flex: 1 }}>
            <Table stickyHeader aria-label="sticky-history-table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  {columnsToDisplay.map((column: PlanningColumn) => (
                    <ColumnHeaderCell
                      key={column.id}
                      column={column}
                      activeFilter={history.filters.find(
                        (filter: Filter) => filter.id === column.id
                      )}
                      onSort={onSort}
                      sorting={sorting}
                    />
                  ))}
                </TableRow>
              </TableHead>
              <TableBody id={'planning-table-body'}>
                {plannings.map((row: Planning) => {
                  const optId = row.optimization_id;
                  const isSelectedOpt = optId === selected_process?.optimization_id;
                  const expanded = expandedOpt === optId;
                  return (
                    <React.Fragment key={optId}>
                      <TableRow
                        hover
                        selected={isSelectedOpt}
                        sx={{ cursor: 'pointer', height: '62px' }}
                        onClick={() => onSelectDispatchProcess(row)}
                        role="checkbox"
                        id={`planning-row-${optId}`}
                        tabIndex={-1}
                      >
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(optId);
                          }}
                        >
                          <IconButton size="small" aria-label="show versions">
                            {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                          </IconButton>
                        </TableCell>
                        {columnsToDisplay.map((column) => {
                          const value =
                            column.id === 'download'
                              ? row['optimization_id']
                              : (row as any)[column.id];
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.format ? column.format(value) : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>

                      {expanded &&
                        renderVersionRows(
                          optId,
                          row,
                          isSelectedOpt,
                          columnsToDisplay.length,
                        )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};
